import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { EvaluationCriteria, EvaluationCriteriaInput, EvaluationCategory } from '@/types/employeeEvaluations';
import type { EmployeePosition } from '@/types/employees';

interface EvaluationCriteriaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  criteria: EvaluationCriteria | null;
  categories: EvaluationCategory[];
  positions?: EmployeePosition[];
  onSave: (data: EvaluationCriteriaInput) => Promise<void>;
}

export const EvaluationCriteriaDialog: React.FC<EvaluationCriteriaDialogProps> = ({
  open,
  onOpenChange,
  criteria,
  categories,
  positions = [],
  onSave,
}) => {
  const [formData, setFormData] = useState<EvaluationCriteriaInput>({
    code: '',
    name: '',
    description: '',
    category_id: '',
    weight: 3,
    is_active: true,
    apply_to_positions: [],
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (criteria) {
      setFormData({
        code: criteria.code,
        name: criteria.name,
        description: criteria.description || '',
        category_id: criteria.category_id,
        weight: criteria.weight,
        is_active: criteria.is_active,
        apply_to_positions: criteria.apply_to_positions || [],
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        category_id: categories[0]?.id || '',
        weight: 3,
        is_active: true,
        apply_to_positions: [],
      });
    }
  }, [criteria, categories, open]);

  const togglePosition = (positionId: string) => {
    const current = formData.apply_to_positions || [];
    const updated = current.includes(positionId)
      ? current.filter(id => id !== positionId)
      : [...current, positionId];
    setFormData({ ...formData, apply_to_positions: updated });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await onSave(formData);
    setIsSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={criteria ? 'Edit Evaluation Criteria' : 'Add Evaluation Criteria'}
            popupContext="/internal-training/evaluation-criteria"
            isOpen={open}
          >
            {criteria ? 'Edit Criteria' : 'Add Criteria'}
          </DialogTitleWithBug>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                placeholder="e.g., CAT"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                maxLength={10}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(v) => setFormData({ ...formData, category_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Completed assignments on time"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe this criteria..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Weight</Label>
              <span className="text-sm font-medium">{formData.weight}</span>
            </div>
            <Slider
              value={[formData.weight]}
              onValueChange={([v]) => setFormData({ ...formData, weight: v })}
              min={1}
              max={5}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Low (1)</span>
              <span>Medium (3)</span>
              <span>High (5)</span>
            </div>
          </div>

          {positions.length > 0 && (
            <div className="space-y-3">
              <Label>Apply to Positions</Label>
              <div className="border rounded-md p-3 max-h-32 overflow-y-auto space-y-2">
                {positions.filter(p => p.is_active).map((position) => (
                  <div key={position.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`position-${position.id}`}
                      checked={(formData.apply_to_positions || []).includes(position.id)}
                      onCheckedChange={() => togglePosition(position.id)}
                    />
                    <label 
                      htmlFor={`position-${position.id}`}
                      className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
                    >
                      {position.name}
                      {position.department && (
                        <Badge variant="outline" className="text-xs">{position.department}</Badge>
                      )}
                    </label>
                  </div>
                ))}
              </div>
              {(formData.apply_to_positions || []).length === 0 && (
                <p className="text-xs text-muted-foreground">If no positions are selected, this criteria applies to all positions.</p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="is_active">Active</Label>
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !formData.code || !formData.name || !formData.category_id}>
              {isSaving ? 'Saving...' : criteria ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
